import { createSignal, onMount, onCleanup, For, createMemo, createEffect, Show, untrack } from "solid-js";
import { EmptyState } from '../components/EmptyState';
import { Pagination } from '../components/Pagination';
import { useParams, useNavigate, useSearchParams } from "@solidjs/router";
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import Text from '../components/Text';
import { useItemName } from '../hooks/useItemName';
import { useTranslation } from '../hooks/useTranslation';
import { JobEntry, PriceHistory } from '../models/JobEntry';
import { useUserContext } from '../components/UserContextProvider';
import ItemStackView from '../components/ItemStackView';
import { fromIdentifier } from '../services/itemRefAdapters';
import { useCraftingResolver } from '../services/craftingResolverContext';
import { formatDateTime } from '../services/dateFormat';
import { formatCurrency, formatInteger } from '../services/numberFormat';
import styles from './JobDetails.module.css';

import {
    Chart,
    CategoryScale,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    BarElement
} from 'chart.js';

import moment from 'moment';
import 'chartjs-adapter-moment';

moment.locale('de');

Chart.register(
    CategoryScale,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    BarElement
);

const TABLE_PAGE_SIZE = 25;

function readCssVar(name: string, fallback: string): string {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
}

export default function JobDetails() {
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const api = useApi();
    const toast = useToast();
    const { user } = useUserContext();
    const resolver = useCraftingResolver();
    const { t } = useTranslation();
    const { itemName } = useItemName();

    const [jobEntries, setJobEntries] = createSignal<JobEntry[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [fromDate, setFromDate] = createSignal("");
    const [toDate, setToDate] = createSignal("");
    const [priceChart, setPriceChart] = createSignal<Chart | null>(null);
    const [priceChartCanvas, setPriceChartCanvas] = createSignal<HTMLCanvasElement | null>(null);
    const [tablePage, setTablePage] = createSignal(0);

    let fetchToken = 0;
    let dateDebounce: number | undefined;

    const identifier = () => {
        const raw = decodeURIComponent(params.unlocalizedName || "");
        return raw === '_' ? '' : raw;
    };
    const name = () => {
        const raw = decodeURIComponent(params.name || "");
        return raw === '_' ? '' : raw;
    };
    const metadata = () => {
        const v = searchParams.m;
        const n = typeof v === 'string' ? parseInt(v, 10) : 0;
        return Number.isFinite(n) ? n : 0;
    };

    function today(): string {
        return new Date().toISOString().split('T')[0];
    }

    function daysAgo(days: number): string {
        return new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
    }

    onMount(async () => {
        const fromParam = typeof searchParams.from === 'string' ? searchParams.from : '';
        const toParam = typeof searchParams.to === 'string' ? searchParams.to : '';
        setFromDate(fromParam || daysAgo(30));
        setToDate(toParam || today());
        await fetchJobEntries();
    });

    onCleanup(() => {
        if (dateDebounce) window.clearTimeout(dateDebounce);
        priceChart()?.destroy();
    });

    async function fetchJobEntries() {
        const token = ++fetchToken;
        try {
            setLoading(true);
            const from = fromDate();
            const to = toDate();
            const reqParams = { itemIdentifier: identifier(), name: name(), metadata: metadata() };
            const response = from && to
                ? await api.jobEntry.getJobEntriesRange(from, to, reqParams)
                : await api.jobEntry.getAllJobEntriesByName(reqParams);
            if (token !== fetchToken) return;
            if (isOk(response.data)) {
                setJobEntries((response.data.value.entries ?? []) as JobEntry[]);
                setTablePage(0);
            } else {
                toast?.error(t("JOB_DETAILS_NO_DATA_TITLE"));
            }
        } catch {
            if (token !== fetchToken) return;
            toast?.error(t("JOB_DETAILS_NO_DATA_TITLE"));
        } finally {
            if (token === fetchToken) setLoading(false);
        }
    }

    function scheduleFetch() {
        if (dateDebounce) window.clearTimeout(dateDebounce);
        dateDebounce = window.setTimeout(() => {
            void fetchJobEntries();
        }, 300);
    }

    function updateDate(setter: (v: string) => void, value: string) {
        setter(value);
        const from = setter === setFromDate ? value : fromDate();
        const to = setter === setToDate ? value : toDate();
        setSearchParams({ from, to }, { replace: true });
        scheduleFetch();
    }

    const itemInfo = createMemo(() => {
        const entries = jobEntries();
        if (entries.length === 0) return null;
        return {
            itemIdentifier: entries[0].itemIdentifier,
            name: entries[0].name,
            isCustomBlock: entries[0].isCustomBlock,
        };
    });

    const stats = createMemo(() => {
        const entries = jobEntries();
        if (entries.length === 0) return null;

        const allPrices = entries.flatMap(entry =>
            Object.keys(entry.pricesToStacks).map(price => parseInt(price))
        );
        const allStacks = entries.flatMap(entry =>
            Object.values(entry.pricesToStacks)
        );

        return {
            totalEntries: entries.length,
            highestPrice: allPrices.length > 0 ? Math.max(...allPrices) : 0,
            lowestPrice: allPrices.length > 0 ? Math.min(...allPrices) : 0,
            averagePrice: allPrices.length > 0 ? Math.round(allPrices.reduce((a, b) => a + b, 0) / allPrices.length) : 0,
            maxStacks: allStacks.length > 0 ? Math.max(...allStacks) : 0
        };
    });

    const priceHistory = createMemo<PriceHistory[]>(() => {
        const entries = jobEntries();
        return entries.map(entry => {
            const pricesToStacks = entry.pricesToStacks;
            const priceKeys = Object.keys(pricesToStacks).map(p => parseInt(p));
            const highestPrice = priceKeys.length > 0 ? Math.max(...priceKeys) : 0;
            const lowestPrice = priceKeys.length > 0 ? Math.min(...priceKeys) : 0;
            const averagePrice = priceKeys.length > 0
                ? Math.round(priceKeys.reduce((a, b) => a + b, 0) / priceKeys.length)
                : 0;
            const stackValues = Object.values(pricesToStacks);
            const stacksAverage = stackValues.length > 0
                ? stackValues.reduce((s, v) => s + v, 0) / stackValues.length
                : 0;

            return {
                date: entry.createdAt,
                highestPrice,
                averagePrice,
                lowestPrice,
                stacksHighest: pricesToStacks[highestPrice] || 0,
                stacksAverage,
                stacksLowest: pricesToStacks[lowestPrice] || 0
            } as PriceHistory;
        }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });

    createEffect(() => {
        const history = priceHistory();
        const canvas = priceChartCanvas();
        if (!canvas) return;
        untrack(() => {
            if (history.length === 0) {
                priceChart()?.destroy();
                setPriceChart(null);
                const ctx = canvas.getContext('2d');
                ctx?.clearRect(0, 0, canvas.width, canvas.height);
                return;
            }
            requestAnimationFrame(() => createPriceChart(history));
        });
    });

    function createPriceChart(history: PriceHistory[]) {
        const canvas = priceChartCanvas();
        if (!canvas) return;
        priceChart()?.destroy();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const textPrimary = readCssVar('--dark-text-primary', '#e0e0e0');
        const textMuted = readCssVar('--dark-text-muted', '#999999');
        const bgElevated = readCssVar('--dark-bg-elevated', 'rgba(30,30,30,0.95)');
        const borderColor = readCssVar('--dark-border', '#444444');
        const gridColor = 'rgba(255, 255, 255, 0.05)';

        function computeLinearRegression(xs: number[], ys: number[]) {
            const n = xs.length;
            if (n === 0) return null;
            const xMean = xs.reduce((a, b) => a + b, 0) / n;
            const yMean = ys.reduce((a, b) => a + b, 0) / n;
            let num = 0;
            let den = 0;
            for (let i = 0; i < n; i++) {
                const dx = xs[i] - xMean;
                num += dx * (ys[i] - yMean);
                den += dx * dx;
            }
            if (den === 0) return null;
            const slope = num / den;
            const intercept = yMean - slope * xMean;
            return { slope, intercept };
        }

        const xs = history.map(h => new Date(h.date).getTime());
        const ys = history.map(h => h.highestPrice);
        const regression = computeLinearRegression(xs, ys);

        const axisMin = fromDate() ? new Date(fromDate() + 'T00:00:00') : undefined;
        const axisMax = toDate()
            ? (() => { const d = new Date(toDate() + 'T00:00:00'); d.setDate(d.getDate() + 1); return d; })()
            : undefined;

        const labelHighest = user().language?.values['HIGHEST_PRICE'] ?? 'Highest Price';
        const labelAverage = user().language?.values['AVG_PRICE'] ?? 'Average Price';
        const labelLowest = user().language?.values['LOWEST_PRICE'] ?? 'Lowest Price';
        const labelTrend = user().language?.values['TREND_LINE'] ?? 'Trend (highest)';

        const regressionDataset = regression
            ? {
                  label: labelTrend,
                  data: history.map(h => ({ x: new Date(h.date).toISOString(), y: regression.slope * new Date(h.date).getTime() + regression.intercept })),
                  borderColor: '#ffd54f',
                  borderWidth: 2,
                  borderDash: [6, 4],
                  pointRadius: 0,
                  fill: false,
                  tension: 0
              }
            : null;

        const stackLabel = t("JOB_DETAILS_STACKS_SUFFIX");

        const chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: history.map(h => new Date(h.date).toISOString()),
                datasets: [
                    {
                        label: labelHighest,
                        data: history.map(h => ({ x: new Date(h.date).toISOString(), y: h.highestPrice })),
                        borderColor: '#60a5fa',
                        backgroundColor: 'rgba(96, 165, 250, 0.1)',
                        borderWidth: 2,
                        fill: false,
                        tension: 0.2,
                        pointBackgroundColor: '#60a5fa',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        pointHoverRadius: 5
                    },
                    {
                        label: labelAverage,
                        data: history.map(h => ({ x: new Date(h.date).toISOString(), y: h.averagePrice })),
                        borderColor: '#7c4dff',
                        backgroundColor: 'rgba(124, 77, 255, 0.1)',
                        borderWidth: 2,
                        fill: false,
                        tension: 0.4,
                        pointBackgroundColor: '#7c4dff',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        hidden: true
                    },
                    {
                        label: labelLowest,
                        data: history.map(h => ({ x: new Date(h.date).toISOString(), y: h.lowestPrice })),
                        borderColor: '#ef4444',
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        borderWidth: 2,
                        fill: false,
                        tension: 0.2,
                        pointBackgroundColor: '#ef4444',
                        pointBorderColor: '#ffffff',
                        pointBorderWidth: 0,
                        pointRadius: 0,
                        pointHoverRadius: 5,
                        hidden: true
                    },
                    ...(regressionDataset ? [regressionDataset] : [])
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 600, easing: 'easeOutCubic' },
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    title: { display: false },
                    legend: {
                        labels: { color: textPrimary }
                    },
                    tooltip: {
                        backgroundColor: bgElevated,
                        titleColor: textPrimary,
                        bodyColor: textPrimary,
                        borderColor: borderColor,
                        borderWidth: 1,
                        callbacks: {
                            afterLabel: (context) => {
                                const index = context.dataIndex;
                                const datasetLabel = context.dataset.label;
                                const stackMap: Record<string, keyof PriceHistory> = {
                                    [labelHighest]: 'stacksHighest',
                                    [labelAverage]: 'stacksAverage',
                                    [labelLowest]: 'stacksLowest'
                                };
                                const stackField = datasetLabel ? stackMap[datasetLabel] : undefined;
                                if (stackField !== undefined && history[index][stackField] !== undefined) {
                                    const stacksValue = history[index][stackField];
                                    return `${stackLabel}: ${stacksValue}`;
                                }
                                return '';
                            }
                        }
                    }
                },
                scales: {
                    x: {
                        type: 'time',
                        min: axisMin?.getTime(),
                        max: axisMax?.getTime(),
                        adapters: {
                            date: { locale: 'de' }
                        },
                        time: {
                            tooltipFormat: 'DD.MM.YYYY'
                        },
                        ticks: { color: textMuted },
                        grid: { color: gridColor }
                    },
                    y: {
                        ticks: {
                            color: textMuted,
                            callback: function(value) {
                                return formatCurrency(Number(value));
                            }
                        },
                        grid: { color: gridColor }
                    }
                }
            }
        });

        setPriceChart(chart as any);
    }

    function getItemDisplayName(info: { name: string; itemIdentifier: string; isCustomBlock: boolean }): string {
        if (info.isCustomBlock) return info.name;
        const r = resolver();
        if (r) {
            const bare = info.itemIdentifier.replace(/^minecraft:/, '');
            const match = r.getAllItems?.()?.find(i => i.name === bare && i.metadata === metadata())
                       ?? r.getAllItems?.()?.find(i => i.name === bare);
            if (match) return itemName(match.key);
        }
        return info.name || info.itemIdentifier;
    }

    function setRange(from: string, to: string) {
        setFromDate(from);
        setToDate(to);
        setSearchParams({ from, to }, { replace: true });
        scheduleFetch();
    }

    function setRangeLastWeek() {
        setRange(daysAgo(7), today());
    }

    function setRangeLastMonth() {
        const now = new Date();
        const lastMonth = new Date();
        lastMonth.setMonth(now.getMonth() - 1);
        setRange(lastMonth.toISOString().split('T')[0], today());
    }

    function setRangeLastYear() {
        const now = new Date();
        const lastYear = new Date();
        lastYear.setFullYear(now.getFullYear() - 1);
        setRange(lastYear.toISOString().split('T')[0], today());
    }

    const tableEntries = createMemo(() => jobEntries());
    const tableTotalPages = createMemo(() =>
        Math.max(1, Math.ceil(tableEntries().length / TABLE_PAGE_SIZE))
    );
    const tablePaged = createMemo(() => {
        const p = Math.min(tablePage(), tableTotalPages() - 1);
        const start = p * TABLE_PAGE_SIZE;
        return tableEntries().slice(start, start + TABLE_PAGE_SIZE);
    });

    return (
        <div class={styles.container}>
            <button
                class={styles.backBtn}
                onClick={() => navigate('/jobs')}
            >
                <i class="fa-solid fa-arrow-left" />
                <Text val="BACK_TO_JOBS" />
            </button>

            <Show
                when={!loading()}
                fallback={
                    <div class={styles.loading}>
                        <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
                        <span><Text val="JOB_DETAILS_LOADING" /></span>
                    </div>
                }
            >
                <Show
                    when={itemInfo()}
                    fallback={
                        <EmptyState illustration="jobs" title={t('JOB_DETAILS_NO_DATA_TITLE')} hint={t('JOB_DETAILS_NO_DATA_DESC')} />
                    }
                >
                    <div class={styles.itemHeader}>
                        <div class={styles.itemIcon}>
                            <ItemStackView stack={fromIdentifier(itemInfo()!.itemIdentifier, metadata(), resolver())} />
                        </div>
                        <div class={styles.itemTitleSection}>
                            <h1 class={styles.itemTitle}>{getItemDisplayName(itemInfo()!)}</h1>
                            <Show when={itemInfo()!.name && itemInfo()!.name !== getItemDisplayName(itemInfo()!)}>
                                <p class={styles.itemMeta}>{itemInfo()!.name}</p>
                            </Show>
                            <Show when={itemInfo()!.itemIdentifier}>
                                <p class={styles.itemIdentifier}>{itemInfo()!.itemIdentifier}</p>
                            </Show>
                        </div>
                    </div>

                    <div class={styles.controls}>
                        <div class={styles.dateRange}>
                            <input
                                type="date"
                                class={styles.dateInput}
                                value={fromDate()}
                                onChange={(e) => updateDate(setFromDate, (e.target as HTMLInputElement).value)}
                            />
                            <span class={styles.dateSeparator}>
                                <Text val="JOB_DETAILS_DATE_RANGE_TO" />
                            </span>
                            <input
                                type="date"
                                class={styles.dateInput}
                                value={toDate()}
                                onChange={(e) => updateDate(setToDate, (e.target as HTMLInputElement).value)}
                            />
                        </div>
                        <div class={styles.quickRanges}>
                            <button class="btn btn-secondary btn-sm" onClick={setRangeLastWeek}>
                                <Text val="LAST_WEEK" />
                            </button>
                            <button class="btn btn-secondary btn-sm" onClick={setRangeLastMonth}>
                                <Text val="LAST_MONTH" />
                            </button>
                            <button class="btn btn-secondary btn-sm" onClick={setRangeLastYear}>
                                <Text val="LAST_YEAR" />
                            </button>
                        </div>
                    </div>

                    <Show when={stats()}>
                        <div class={styles.statsGrid}>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatInteger(stats()!.totalEntries)}</div>
                                <div class={styles.statCardLabel}><Text val="TOTAL_ENTRIES" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatCurrency(stats()!.highestPrice)}</div>
                                <div class={styles.statCardLabel}><Text val="HIGHEST_PRICE" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatCurrency(stats()!.averagePrice)}</div>
                                <div class={styles.statCardLabel}><Text val="AVG_PRICE" /></div>
                            </div>
                            <div class={styles.statCard}>
                                <div class={styles.statCardValue}>{formatInteger(stats()!.maxStacks)}</div>
                                <div class={styles.statCardLabel}><Text val="MAX_STACKS" /></div>
                            </div>
                        </div>
                    </Show>

                    <div class={styles.chartSection}>
                        <div class={styles.chartHeader}>
                            <h3 class={styles.chartTitle}><Text val="PRICE_HISTORY" /></h3>
                            <i
                                class={`fa-solid fa-circle-info ${styles.infoIcon}`}
                                title={t("JOB_DETAILS_TREND_INFO")}
                                aria-label={t("JOB_DETAILS_TREND_INFO")}
                            ></i>
                        </div>
                        <Show
                            when={priceHistory().length > 0}
                            fallback={
                                <div class={styles.chartEmpty}>
                                    <i class="fa-solid fa-chart-line"></i>
                                    <p><Text val="JOB_DETAILS_NO_DATA_IN_RANGE" /></p>
                                </div>
                            }
                        >
                            <div class={styles.chartCanvasWrap}>
                                <canvas ref={el => setPriceChartCanvas(el)}></canvas>
                            </div>
                        </Show>
                    </div>

                    <div class={styles.tableSection}>
                        <h3 class={styles.tableTitle}><Text val="JOB_DETAILS_CONTRACT_HISTORY" /></h3>
                        <div class={styles.tableWrap}>
                            <table class={styles.entriesTable}>
                                <caption class={styles.srOnly}><Text val="JOB_DETAILS_TABLE_CAPTION" /></caption>
                                <thead>
                                    <tr>
                                        <th scope="col"><Text val="JOB_DETAILS_TABLE_DATE" /></th>
                                        <th scope="col"><Text val="JOB_DETAILS_TABLE_CONTRACTS" /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <For each={tablePaged()}>
                                        {(entry) => (
                                            <tr>
                                                <td class={styles.dateCell}>{formatDateTime(entry.createdAt)}</td>
                                                <td>
                                                    <div class={styles.priceStackList}>
                                                        <For each={Object.entries(entry.pricesToStacks)}>
                                                            {([price, stacks]) => (
                                                                <div class={styles.priceStackPair}>
                                                                    <span class={styles.price}>{formatCurrency(parseInt(price))}</span>
                                                                    <span class={styles.stacks}>
                                                                        {formatInteger(stacks as number)} <Text val="JOB_DETAILS_STACKS_SUFFIX" />
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </For>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </For>
                                </tbody>
                            </table>
                        </div>
                        <Pagination page={tablePage()} totalPages={tableTotalPages()} onChange={setTablePage} size="sm" />
                    </div>
                </Show>
            </Show>
        </div>
    );
}
